/**
 * BattleHUD — renders HP bars, messages,
 * word preview, submit button, and combatant sprites/labels.
 *
 * Supports multiple enemies positioned across the right side.
 */

declare const Phaser: any;

import { getLetterValue, scoreWord } from "./LetterScoring";
import { generateAllSprites } from "../../sprites/SpriteFactory";
import { ParallaxBackground } from "../../../framework/ParallaxBackground";
import { Depth } from "../../../framework/Depth";

export interface CombatantInfo {
  hp: number;
  maxHp: number;
  label: string;
  level?: number;
  xpProgress?: number;
}

export interface EnemyInfo {
  hp: number;
  maxHp: number;
  attack: number;
  armor: number;
  label: string;
  tier: "normal" | "elite" | "boss";
  level: number;
}

/** Per-enemy visual state. */
export interface EnemySlot {
  sprite: any;
  hpBar: any;
  hpText: any;
  intentIcon: any;
  intentText: any;
  armorIcon: any | null;
  armorText: any | null;
  labelText: any;
  x: number;
  y: number;
}

export class BattleHUD {
  private scene: any;

  // Player side
  private playerHpBar: any;
  private playerHpText: any;
  playerSprite: any;
  playerX = 150;

  // Enemy slots
  enemySlots: EnemySlot[] = [];
  combatantY = 300;

  // Word area
  private wordContainer: any;
  private wordTotalText: any;
  private wordClickZone: any;
  private submitEnabled = false;
  private messageText: any;

  // Gold display (top-left)
  private goldText: any;

  // XP display
  private xpBar: any;
  private levelText: any;
  private barLeft = 90;
  private barW = 120;

  // Level-up indicator
  private lvlUpIcon: any | null = null;
  private lvlUpText: any | null = null;
  private lvlUpTween: any | null = null;

  // AP display
  private apPips: any[] = [];
  private apText: any;

  // End turn button
  private endTurnBtn: any;
  private endTurnLabel: any;

  // Shuffle button
  private shuffleBtn: any;
  private shuffleLabel: any;
  private onShuffle: (() => void) | null = null;

  private previewY = 15;
  private onSubmit: (() => void) | null = null;
  private onEndTurn: (() => void) | null = null;

  constructor(scene: any) {
    this.scene = scene;
  }

  create(
    player: CombatantInfo,
    enemies: EnemyInfo[],
    isBoss: boolean,
    isElite: boolean,
    ap: number,
    maxAp: number,
    previewY: number,
    gold: number,
    shuffleCount: number,
    onSubmit: () => void,
    onEndTurn: () => void,
    onShuffle: () => void
  ): void {
    this.previewY = previewY;
    this.onSubmit = onSubmit;
    this.onEndTurn = onEndTurn;
    this.onShuffle = onShuffle;

    // Parallax background
    this.drawBackground();

    this.drawGold(gold);
    this.drawCombatants(player, enemies, isBoss, isElite, ap, maxAp);
    this.drawWordPreview();
    this.drawEndTurnButton();
    this.drawShuffleButton(shuffleCount);
    this.drawMessageArea();
  }

  // --- Public API ---

  updateWordPreview(word: string): void {
    this.wordContainer.removeAll(true);

    if (word.length === 0) {
      this.wordTotalText.setText("");
      this.wordClickZone.setSize(0, 0);
      this.wordClickZone.disableInteractive();
      return;
    }

    const tileW = 34;
    const tileH = 38;
    const gap = 4;
    const totalW = word.length * (tileW + gap) - gap;
    const startX = -totalW / 2;
    const tileColor = this.submitEnabled ? 0x1d4ed8 : 0x374151;
    const strokeColor = this.submitEnabled ? 0x3b82f6 : 0x4b5563;

    for (let i = 0; i < word.length; i++) {
      const ch = word[i];
      const x = startX + i * (tileW + gap) + tileW / 2;

      const bg = this.scene.add.rectangle(x, 0, tileW, tileH, tileColor);
      bg.setStrokeStyle(1, strokeColor);

      const letter = this.scene.add.text(x - 2, -3, ch.toUpperCase(), {
        fontFamily: "system-ui, sans-serif",
        fontSize: "16px",
        color: "#e5e7eb",
        fontStyle: "bold",
      }).setOrigin(0.5);

      const val = getLetterValue(ch);
      const score = this.scene.add.text(x + tileW / 2 - 5, tileH / 2 - 5, String(val), {
        fontFamily: "system-ui, sans-serif",
        fontSize: "9px",
        color: "#93c5fd",
      }).setOrigin(1, 1);

      this.wordContainer.add([bg, letter, score]);
    }

    const total = scoreWord(word);
    this.wordTotalText.setText(`= ${total}`);
    this.wordTotalText.setX(400 + totalW / 2 + 10);

    // Update click zone to cover the tiles
    this.wordClickZone.setSize(totalW + 8, tileH + 4);
    if (this.submitEnabled) {
      this.wordClickZone.setInteractive({ useHandCursor: true });
    } else {
      this.wordClickZone.disableInteractive();
    }
  }

  updateSubmitEnabled(enabled: boolean): void {
    this.submitEnabled = enabled;
    this.wordContainer.setAlpha(enabled ? 1 : 0.5);
    if (enabled) {
      this.wordClickZone.setInteractive({ useHandCursor: true });
    } else {
      this.wordClickZone.disableInteractive();
    }
  }

  updatePlayerHp(hp: number, maxHp: number): void {
    const ratio = hp / maxHp;
    this.playerHpBar.setDisplaySize(120 * ratio, 6);
    this.playerHpText.setText(`${hp}/${maxHp}`);
  }

  updateEnemyHp(index: number, hp: number, maxHp: number): void {
    const slot = this.enemySlots[index];
    if (!slot) return;
    const ratio = hp / maxHp;
    slot.hpBar.setDisplaySize(100 * ratio, 6);
    slot.hpText.setText(`${hp}/${maxHp}`);
  }

  updateEnemyArmor(index: number, armor: number): void {
    const slot = this.enemySlots[index];
    if (!slot) return;
    if (armor <= 0) {
      slot.armorIcon?.setVisible(false);
      slot.armorText?.setVisible(false);
    } else if (slot.armorText) {
      slot.armorText.setText(`${armor}`);
    }
  }

  /** Update the gold display. */
  updateGold(gold: number): void {
    this.goldText.setText(`${gold}g`);
  }

  /** Update the XP bar fill (0–1 progress). */
  updateXp(progress: number): void {
    const w = this.barW * Math.max(0.001, Math.min(1, progress));
    this.xpBar.setDisplaySize(w, 3);
  }

  /** Update the level display text. */
  updateLevel(level: number): void {
    this.levelText.setText(`Lv.${level}`);
  }

  /** Show a pulsing level-up icon next to the player sprite. */
  showLevelUpIcon(onClick: () => void): void {
    if (this.lvlUpIcon) return; // already showing

    const ix = this.playerX + 45;
    const iy = this.combatantY - 30;

    // Up-arrow icon
    this.lvlUpIcon = this.scene.add.triangle(ix, iy, 0, 12, 7, 0, 14, 12, 0xfbbf24)
      .setDepth(Depth.HUD)
      .setInteractive({ useHandCursor: true });
    this.lvlUpIcon.on("pointerdown", onClick);

    this.lvlUpText = this.scene.add.text(ix, iy + 14, "LVL UP", {
      fontFamily: "system-ui, sans-serif", fontSize: "8px", color: "#fbbf24", fontStyle: "bold",
    }).setOrigin(0.5, 0).setDepth(Depth.HUD);

    // Pulse animation
    this.lvlUpTween = this.scene.tweens.add({
      targets: [this.lvlUpIcon, this.lvlUpText],
      scaleX: 1.2, scaleY: 1.2,
      alpha: 0.7,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  /** Remove the level-up icon. */
  hideLevelUpIcon(): void {
    if (this.lvlUpTween) { this.lvlUpTween.stop(); this.lvlUpTween = null; }
    if (this.lvlUpIcon) { this.lvlUpIcon.removeInteractive(); this.lvlUpIcon.destroy(); this.lvlUpIcon = null; }
    if (this.lvlUpText) { this.lvlUpText.destroy(); this.lvlUpText = null; }
  }

  showMessage(msg: string): void {
    this.messageText.setText(msg);
  }

  /**
   * Show a damage calculation breakdown in the message area.
   * e.g.  "QUEST" 14 +1 ATK ×1.2 → 18
   */
  showDamageBreakdown(
    word: string,
    result: { wordScore: number; attackBonus: number; lengthMult: number; adjMult: number; damage: number; threshold: { damageMultiplier: number; damageBonus: number; name: string } | null },
  ): void {
    let parts = `"${word.toUpperCase()}" ${result.wordScore}`;
    if (result.attackBonus > 0) parts += ` +${result.attackBonus}`;
    if (result.threshold && result.threshold.damageBonus > 0) parts += ` +${result.threshold.damageBonus}`;
    if (result.threshold && result.threshold.damageMultiplier !== 1) parts += ` ×${result.threshold.damageMultiplier}`;
    if (result.lengthMult > 1) parts += ` ×${result.lengthMult}`;
    if (result.adjMult > 1) parts += ` ×${result.adjMult} adj`;
    parts += ` → ${result.damage}`;
    this.messageText.setText(parts);
  }

  /**
   * Extract letter tiles from the word container as independent AttackTile objects.
   */
  extractTiles(word: string): import("./attacks/AttackType").AttackTile[] {
    const tiles: import("./attacks/AttackType").AttackTile[] = [];
    const objectsPerTile = 3; // bg, letter, score
    const children = this.wordContainer.getAll();
    const containerX = this.wordContainer.x;
    const containerY = this.wordContainer.y;

    for (let i = 0; i < word.length; i++) {
      const baseIdx = i * objectsPerTile;
      const bg = children[baseIdx];
      const letterText = children[baseIdx + 1];
      const scoreText = children[baseIdx + 2];
      const objects = [bg, letterText, scoreText];

      const worldX = containerX + bg.x;
      const worldY = containerY + (bg.y || 0);

      tiles.push({
        letter: word[i],
        value: getLetterValue(word[i]),
        objects,
        worldX,
        worldY,
      });
    }

    // Pull objects out of the container and into the scene directly
    for (const tile of tiles) {
      for (const obj of tile.objects) {
        const localX = obj.x;
        const localY = obj.y;
        this.wordContainer.remove(obj);
        obj.setPosition(containerX + localX, containerY + localY);
        this.scene.add.existing(obj);
      }
    }

    // Clean up container and total text
    this.wordContainer.removeAll(true);
    this.wordTotalText.setText("");
    this.wordTotalText.setAlpha(1);
    this.wordClickZone.setSize(0, 0);
    this.wordClickZone.disableInteractive();

    return tiles;
  }

  updateAp(ap: number, maxAp: number): void {
    for (let i = 0; i < this.apPips.length; i++) {
      this.apPips[i].setFillStyle(i < ap ? 0xfbbf24 : 0x374151);
    }
    this.apText.setText(`${ap}/${maxAp} AP`);
  }

  showEndTurn(visible: boolean): void {
    this.endTurnBtn.setVisible(visible);
    this.endTurnLabel.setVisible(visible);
  }

  updateShuffle(count: number): void {
    this.shuffleLabel.setText(`SHUFFLE (${count})`);
    if (count <= 0) {
      this.shuffleBtn.removeInteractive();
      this.shuffleBtn.setAlpha(0.3);
      this.shuffleLabel.setAlpha(0.3);
    }
  }

  /** Toggle player turn UI (end turn button + submit). */
  setPlayerTurnActive(active: boolean): void {
    this.showEndTurn(active);
    if (!active) {
      this.updateSubmitEnabled(false);
    }
  }

  /** Get the slot for a specific enemy (by index into combat.enemies). */
  getSlot(index: number): EnemySlot | null {
    return this.enemySlots[index] ?? null;
  }

  // --- Drawing ---

  private drawBackground(): void {
    const bg = new ParallaxBackground(this.scene);

    // Sky
    bg.addFill(0x0a0e1a);
    bg.addFill(0x111b2e, { y: 200, height: 80, alpha: 0.7 });

    // Clouds — drifting
    bg.addImage("bg_clouds1", { y: 170, scaleX: 0.9, scaleY: 0.5, tint: 0x1a2744, alpha: 0.4, drift: 30 });
    bg.addImage("bg_clouds2", { y: 185, scaleX: 1.0, scaleY: 0.4, tint: 0x1e3050, alpha: 0.3, drift: -20, driftDuration: 15000 });

    // Mountains — offset left and right
    bg.addImage("bg_mountain1", { x: 220, y: 220, scaleX: 0.7, scaleY: 0.5, tint: 0x111927, alpha: 0.6 });
    bg.addImage("bg_mountain2", { x: 580, y: 225, scaleX: 0.6, scaleY: 0.45, tint: 0x0f1520, alpha: 0.5 });

    // Hills
    bg.addImage("bg_hills1", { y: 250, scaleX: 1.0, scaleY: 0.4, tint: 0x141e2e, alpha: 0.7 });
    bg.addImage("bg_hills2", { y: 265, scaleX: 1.1, scaleY: 0.45, tint: 0x0f1722, alpha: 0.8 });

    // Ground
    bg.addFill(0x0c1118, { y: 340, height: 160 });
  }

  private drawGold(gold: number): void {
    this.goldText = this.scene.add.text(14, 10, `${gold}g`, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "13px",
      color: "#fbbf24",
      fontStyle: "bold",
    }).setDepth(Depth.HUD);
  }

  private drawCombatants(
    player: CombatantInfo,
    enemies: EnemyInfo[],
    isBoss: boolean,
    isElite: boolean,
    ap: number,
    maxAp: number
  ): void {
    const topY = 300;
    const pX = 150;
    this.playerX = pX;
    this.combatantY = topY;
    const barW = 120;
    const barLeft = pX - barW / 2;
    this.barW = barW;
    this.barLeft = barLeft;

    // Player sprite + label with level
    this.drawPlayerSprite(pX, topY);
    const levelNum = player.level ?? 1;
    this.levelText = this.scene.add.text(pX, topY + 42, `Lv.${levelNum}`, {
      fontFamily: "system-ui, sans-serif", fontSize: "11px", color: "#9ca3af",
    }).setOrigin(0.5).setDepth(Depth.HUD);

    // HP bar
    const barY = topY + 56;
    this.scene.add.rectangle(pX, barY, barW + 4, 10, 0x374151).setOrigin(0.5).setDepth(Depth.HUD);
    this.playerHpBar = this.scene.add.rectangle(barLeft, barY, barW, 6, 0x22c55e).setOrigin(0, 0.5).setDepth(Depth.HUD);

    // XP bar — thin bar just below HP bar
    const xpBarY = barY + 8;
    this.scene.add.rectangle(pX, xpBarY, barW + 4, 5, 0x1e293b).setOrigin(0.5).setDepth(Depth.HUD);
    const xpProgress = player.xpProgress ?? 0;
    this.xpBar = this.scene.add.rectangle(barLeft, xpBarY, barW, 3, 0x8b5cf6).setOrigin(0, 0.5).setDepth(Depth.HUD);
    this.xpBar.setDisplaySize(barW * Math.max(0.001, xpProgress), 3);

    // Below the bars: HP text on left, AP pips on right
    const infoY = xpBarY + 9;
    this.playerHpText = this.scene.add.text(barLeft, infoY, `${player.hp}/${player.maxHp}`, {
      fontFamily: "system-ui, sans-serif", fontSize: "10px", color: "#22c55e",
    }).setOrigin(0, 0.5).setDepth(Depth.HUD);
    this.updatePlayerHp(player.hp, player.maxHp);

    // AP pips — right-aligned under bar
    this.apPips = [];
    const pipSpacing = 14;
    const pipsWidth = (maxAp - 1) * pipSpacing;
    const pipStartX = barLeft + barW - pipsWidth;
    for (let i = 0; i < maxAp; i++) {
      const pip = this.scene.add.circle(pipStartX + i * pipSpacing, infoY, 5, i < ap ? 0xfbbf24 : 0x374151);
      pip.setStrokeStyle(1, 0x78716c);
      pip.setDepth(Depth.HUD);
      this.apPips.push(pip);
    }
    this.apText = this.scene.add.text(barLeft + barW, infoY + 10, `${ap}/${maxAp}`, {
      fontFamily: "system-ui, sans-serif", fontSize: "9px", color: "#fbbf24",
    }).setOrigin(1, 0.5).setDepth(Depth.HUD);

    // Enemies — spread across the right side
    this.enemySlots = [];
    this.drawEnemies(enemies, topY, isBoss, isElite);
  }

  private drawEnemies(enemies: EnemyInfo[], topY: number, isBoss: boolean, isElite: boolean): void {
    if (!this.scene.textures.exists("enemy_slime")) {
      generateAllSprites(this.scene);
    }

    const count = enemies.length;
    // Center enemies around x=650, spread them apart
    const spacing = count === 1 ? 0 : 120;
    const groupWidth = (count - 1) * spacing;
    const startX = 650 - groupWidth / 2;
    const eBarW = 100;

    for (let i = 0; i < count; i++) {
      const enemy = enemies[i];
      const eX = startX + i * spacing;

      // Sprite
      const key = enemy.tier === "boss" ? "enemy_dragon" : enemy.tier === "elite" ? "enemy_goblin" : "enemy_slime";
      const scale = enemy.tier === "boss" ? 4.5 : 4;
      const sprite = this.scene.add.image(eX, topY, key).setScale(scale).setDepth(Depth.GAME);

      // Intent: sword icon + damage number
      const intentY = topY - 50;
      const intentIcon = this.scene.add.image(eX - 12, intentY, "icon_battle").setScale(2).setDepth(Depth.HUD);
      intentIcon.setTint(0xef4444);
      const intentText = this.scene.add.text(eX + 4, intentY, `${enemy.attack}`, {
        fontFamily: "system-ui, sans-serif", fontSize: "14px", color: "#f87171", fontStyle: "bold",
      }).setOrigin(0, 0.5).setDepth(Depth.HUD);

      // Armor — shield icon + number (only shown if armor > 0)
      let armorIcon: any = null;
      let armorText: any = null;
      if (enemy.armor > 0) {
        const armorY = intentY + 18;
        armorIcon = this.scene.add.rectangle(eX - 12, armorY, 14, 14, 0x6b7280, 0.9).setDepth(Depth.HUD);
        armorIcon.setStrokeStyle(1, 0x94a3b8);
        armorText = this.scene.add.text(eX + 4, armorY, `${enemy.armor}`, {
          fontFamily: "system-ui, sans-serif", fontSize: "12px", color: "#94a3b8", fontStyle: "bold",
        }).setOrigin(0, 0.5).setDepth(Depth.HUD);
      }

      // Label with level
      const tierLabel = enemy.tier === "boss" ? "BOSS" : enemy.tier === "elite" ? "ELITE" : "Enemy";
      const labelText = this.scene.add.text(eX, topY + 42, `Lv.${enemy.level} ${tierLabel}`, {
        fontFamily: "system-ui, sans-serif", fontSize: "11px", color: "#9ca3af",
      }).setOrigin(0.5).setDepth(Depth.HUD);

      // HP bar
      const eBarY = topY + 56;
      this.scene.add.rectangle(eX, eBarY, eBarW + 4, 10, 0x374151).setOrigin(0.5).setDepth(Depth.HUD);
      const hpBar = this.scene.add.rectangle(eX - eBarW / 2, eBarY, eBarW, 6, 0xef4444).setOrigin(0, 0.5).setDepth(Depth.HUD);

      const hpText = this.scene.add.text(eX - eBarW / 2, eBarY + 12, `${enemy.hp}/${enemy.maxHp}`, {
        fontFamily: "system-ui, sans-serif", fontSize: "10px", color: "#ef4444",
      }).setOrigin(0, 0.5).setDepth(Depth.HUD);

      this.enemySlots.push({
        sprite,
        hpBar,
        hpText,
        intentIcon,
        intentText,
        armorIcon,
        armorText,
        labelText,
        x: eX,
        y: topY,
      });
    }
  }

  private drawPlayerSprite(x: number, y: number): void {
    if (!this.scene.textures.exists("player")) {
      generateAllSprites(this.scene);
    }
    this.playerSprite = this.scene.add.image(x, y, "player").setScale(4).setDepth(Depth.GAME);
  }

  private drawWordPreview(): void {
    const y = this.previewY;
    this.wordContainer = this.scene.add.container(400, y).setDepth(Depth.HUD);

    // Invisible click zone over the word tiles
    this.wordClickZone = this.scene.add.rectangle(400, y, 0, 0, 0x000000, 0).setDepth(Depth.HUD);
    this.wordClickZone.on("pointerdown", () => {
      if (this.submitEnabled) this.onSubmit?.();
    });

    this.wordTotalText = this.scene.add.text(400, y, "", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "13px",
      color: "#fbbf24",
      fontStyle: "bold",
    }).setOrigin(0, 0.5).setDepth(Depth.HUD);
  }

  private drawEndTurnButton(): void {
    // Bottom-left, under the player side, out of the way of word preview
    const btnX = 60;
    const btnY = 430;
    this.endTurnBtn = this.scene.add.rectangle(btnX, btnY, 100, 28, 0xd97706, 0.9).setDepth(Depth.HUD);
    this.endTurnBtn.setStrokeStyle(1, 0xf59e0b);
    this.endTurnBtn.setInteractive({ useHandCursor: true });
    this.endTurnLabel = this.scene.add.text(btnX, btnY, "END TURN", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "11px",
      color: "#ffffff",
      fontStyle: "bold",
    }).setOrigin(0.5).setDepth(Depth.HUD);
    this.endTurnBtn.on("pointerdown", () => this.onEndTurn?.());
    this.showEndTurn(false);
  }

  private drawShuffleButton(count: number): void {
    const btnX = 740;
    const btnY = 430;
    this.shuffleBtn = this.scene.add.rectangle(btnX, btnY, 110, 28, 0x7c3aed, 0.9).setDepth(Depth.HUD);
    this.shuffleBtn.setStrokeStyle(1, 0x8b5cf6);
    this.shuffleLabel = this.scene.add.text(btnX, btnY, `SHUFFLE (${count})`, {
      fontFamily: "system-ui, sans-serif",
      fontSize: "11px",
      color: "#ffffff",
      fontStyle: "bold",
    }).setOrigin(0.5).setDepth(Depth.HUD);
    if (count > 0) {
      this.shuffleBtn.setInteractive({ useHandCursor: true });
      this.shuffleBtn.on("pointerdown", () => this.onShuffle?.());
    } else {
      this.shuffleBtn.setAlpha(0.3);
      this.shuffleLabel.setAlpha(0.3);
    }
  }

  private drawMessageArea(): void {
    // Position above the word preview, below the combatants
    const msgY = this.previewY - 32;
    this.messageText = this.scene.add.text(400, msgY, "", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "16px",
      color: "#d1d5db",
    }).setOrigin(0.5).setDepth(Depth.HUD);
  }
}
